/**
 * What each conversation's agent last saw of the document, and what changed in
 * the document since, ready before the user sends.
 *
 * WHY
 *   The agent reads the whole document when a conversation starts and when it
 *   asks for it again (`request_focused_document`). Between those reads the user
 *   edits, applies cards and accepts changes, and the agent knows none of it
 *   unless it reads the whole document again. Here the add-in keeps what the
 *   agent last saw, follows the document, and hands each new message the
 *   changes since: every paragraph that changed, before and after, in the form
 *   the agent reads paragraphs in (`DocumentView`).
 *
 * HOW
 *   - every event comes from the document's event stream (`DocumentEvents`);
 *   - the document handed to an agent (`document_sent_to_agent`) becomes that
 *     conversation's view as soon as a whole-body read covers it;
 *   - every whole-body read the add-in makes, whoever made it, becomes the
 *     latest view; after edits settle, and when the user comes back to the pane
 *     (a comment or a tracked deletion raises no Word event), the views read the
 *     body themselves, standing aside while foreground work holds Word, and only
 *     while some conversation has a view to compare against;
 *   - an edit Word reported after the latest read is listed by paragraph,
 *     without its text, as "edited since read";
 *   - each card the user applied (`redlines_applied`) rides with every
 *     conversation's next message, one entry per card with its number, every
 *     paragraph it changed or added shown whole before and after by the bookmarks
 *     the card names, and its comments (`appliedCardsForAgent`); a paragraph a card
 *     shows is not listed again among the changes, and a card the read every apply
 *     starts has not reached yet rides with the first message sent after it does;
 *   - what changed between each conversation's view and the latest view is
 *     worked out when the latest view is parsed (`prepareChangesForEveryConversation`),
 *     so `changesForMessage` answers from what is already worked out, never
 *     touches Word and never aligns two views on the Send click; the
 *     conversation's view moves forward only once the message carrying the
 *     changes was accepted (`markDelivered`).
 */
import type { DocumentChange, DocumentChangesForAgent, ParagraphReference } from "@common/types/document-changes-for-agent";

import { reportError } from "../sentry-reporter";

import { type AppliedCardsForMessage, appliedCardsForAgent, changesNotShownInAppliedCards, type ReportedAppliedCard } from "./applied-cards-for-agent";
import type { AppliedCardReport, DocumentEvent, DocumentEventDelivery, DocumentSentToAgent } from "./document-events";
import { failedReadRetryMs } from "./document-page-store";
import { DocumentView } from "./document-view";
import type { BodyRead, ChangeOrigin, WordDocument } from "./word-document";

export interface AgentDocumentViewsConfig {
    /** Quiet time after the last edit before the body is read: one read for a burst of typing. */
    editsSettleMs: number;
    /** How long a read stands aside while foreground work holds Word before asking again. */
    foregroundRetryMs: number;
    /** Conversations whose view is kept; the least recently used beyond this is forgotten. */
    rememberedConversations: number;
    /** Reported edits kept while no read covers them; the oldest beyond this are forgotten. */
    rememberedEdits: number;
    /** Paragraph changes (changed, added, deleted) one message may carry before it carries only their count. */
    maximumParagraphChanges: number;
    /** Characters of paragraph text one message may carry, before and after counted, before it carries only the count. */
    maximumChangedTextCharacters: number;
    /** Characters of accepted text a whole document may hold and still ride along when the paragraph ids changed; a longer one is left for the agent to read again. */
    maximumResentDocumentCharacters: number;
}

export const DEFAULT_AGENT_DOCUMENT_VIEWS_CONFIG: AgentDocumentViewsConfig = {
    editsSettleMs: 2_000,
    foregroundRetryMs: 250,
    rememberedConversations: 4,
    rememberedEdits: 1_000,
    maximumParagraphChanges: 40,
    maximumChangedTextCharacters: 40_000,
    maximumResentDocumentCharacters: 40_000,
};

/** The document changes a message carries, and how to record that the agent received them. */
export interface ChangesForMessage {
    documentChanges: DocumentChangesForAgent;
    /** The message was accepted: the conversation's view becomes the document these changes describe. */
    markDelivered(): void;
}

/**
 * What one conversation's agent last saw: the view it was handed, the last event its copy
 * covers, and the reports of the cards a message it received went without because no read
 * had reached them yet (`appliedCardsForAgent`), still owed though they fall before that event.
 */
interface ConversationView {
    view: DocumentView;
    agentSawEventsThroughSequence: number;
    appliedCardsHeldBack: ReportedAppliedCard[];
}

/** An edit Word or the typing detector reported, kept until every conversation's view covers it. */
interface ReportedEdit {
    sequence: number;
    edit: "changed" | "added" | "deleted";
    origin: ChangeOrigin;
    uniqueLocalIds: string[];
}

/** The paragraph changes between a conversation's view and the latest view fit the budget: every one, as `changesSince` reports them. */
interface ViewChangesWithinBudget {
    kind: "within_budget";
    changes: DocumentChange[];
}

/** The paragraph changes between a conversation's view and the latest view do not fit the budget: only how many paragraphs changed. */
interface ViewChangesPastBudget {
    kind: "past_budget";
    changedParagraphCount: number;
}

/**
 * The paragraph ids the conversation's agent holds no longer name the paragraphs, for
 * more paragraphs than one message lists (`DocumentView.paragraphIdsChangedSince`,
 * `maximumParagraphChanges`): the message carries the whole document
 * under its new ids when it is short enough (`maximumResentDocumentCharacters`), then
 * the changes aligned by Word's own paragraph ids, or only how many paragraphs changed
 * when those do not fit the budget.
 */
interface ViewChangesUnderNewParagraphIds {
    kind: "paragraph_ids_changed";
    changes: DocumentChange[];
    unlistedChangedParagraphCount: number;
    /** False when the document is too long to ride along: the agent is told its ids changed and reads the document again only when it needs them. */
    documentRidesAlong: boolean;
}

/** What changed from a conversation's view to the latest view, as one message can carry it. */
type ViewChanges = ViewChangesWithinBudget | ViewChangesPastBudget | ViewChangesUnderNewParagraphIds;

/** How many paragraphs a change list changes, and whether one message can carry their text. */
interface ChangeBudget {
    paragraphChangeCount: number;
    fitsOneMessage: boolean;
}

/** A conversation's view changes, worked out when the latest view was parsed, with the two views they lie between. */
interface PreparedViewChanges {
    conversationView: DocumentView;
    latestView: DocumentView;
    viewChanges: ViewChanges;
}

/** A conversation whose view is the latest view: nothing to align, nothing changed. */
const NO_VIEW_CHANGES: ViewChangesWithinBudget = { kind: "within_budget", changes: [] };

/** The owed edits of one kind by one origin: the paragraphs they name, each once, and the ids no view could name. */
interface OwedEditGroup {
    edit: ReportedEdit["edit"];
    origin: ChangeOrigin;
    paragraphByUniqueLocalId: Map<string, ParagraphReference>;
    unidentifiedUniqueLocalIds: Set<string>;
}

/**
 * How the copy handed to an agent relates to the view it is given.
 *
 * CASES
 *   - "view_is_current"     -> nothing changed the document since the view was read: it is the copy,
 *                              whichever was read first
 *   - "view_must_cover_copy" -> the view counts only once its read began after the copy's
 */
type CopyCoverage = "view_is_current" | "view_must_cover_copy";

export class AgentDocumentViews {
    private readonly viewsByConversation = new Map<string, ConversationView>();
    /** Each conversation's changes since its view, worked out when the latest view was parsed: conversation id -> the prepared changes. */
    private readonly preparedViewChangesByConversation = new Map<string, PreparedViewChanges>();
    /** Copies handed to an agent that no whole-body read has caught up with yet: conversation id -> the last event the copy covers. */
    private readonly copiesAwaitingView = new Map<string, number>();
    private reportedEdits: ReportedEdit[] = [];
    private reportedAppliedCards: ReportedAppliedCard[] = [];
    /** The newest view parsed from a whole-body read; null before the first. */
    private latestView: DocumentView | null = null;
    /** The newest whole-body read not parsed yet; parsing waits for the reader's own work to finish. */
    private bodyReadAwaitingParse: BodyRead | null = null;
    private parseTimer: number | null = null;
    /** The sequence of the last event that changed the document: an edit, or a body replacement. */
    private lastDocumentChangeSequence = 0;
    /** The highest sequence any whole-body read received so far covers, parsed or not. */
    private lastReadCoversEventsThroughSequence = 0;
    private readTimer: number | null = null;
    private readInFlight = false;
    private readQueued = false;
    private readFailures = 0;
    private following = false;

    constructor(
        private readonly word: WordDocument,
        private readonly config: AgentDocumentViewsConfig,
    ) {}

    /**
     * Starts following the document's event stream; returns the unsubscribe.
     *
     * WHEN the views already follow the stream
     *   CONSEQUENCE
     *     nothing more is subscribed, and the returned function leaves the one
     *     subscription standing.
     */
    follow(): () => void {
        if (this.following) return () => undefined;
        this.following = true;
        const unsubscribe = this.word.events.subscribe((event, delivery) => this.onDocumentEvent(event, delivery));
        return () => {
            this.following = false;
            unsubscribe();
        };
    }

    /**
     * The changes since this conversation's agent last saw the document, for the
     * message the user is sending now. Answers from the changes worked out when
     * the latest view was parsed; never reads Word, and never aligns two views
     * unless no parse prepared them (`viewChangesFor`).
     *
     * NULL
     *   - the conversation has no view: its agent was never handed the document
     *     in this session, or no read has caught up with the copy it was handed
     *   - nothing changed since its view and no card was applied
     *
     * CASES
     *   - the changes fit the budget -> every change, the view's diff first, then
     *                                   the edits no read has shown yet
     *   - they do not                -> only how many paragraphs changed, and the
     *                                   view stays where it was: the agent has not
     *                                   seen the changes, so they stay owed
     *   - more paragraphs than one
     *     message lists came back
     *     under new ids              -> the whole document under its new ids, then
     *                                   the changes aligned by Word's paragraph ids (or their count),
     *                                   then the edits no read has shown yet
     *   In every case the message also carries the cards the user applied since the
     *   agent's view that a read has reached (`appliedCardsOwedTo`); the others stay owed
     *   to the conversation for a later message.
     */
    changesForMessage(conversationId: string): ChangesForMessage | null {
        const conversationView = this.viewsByConversation.get(conversationId);
        const latestView = this.latestView;
        if (conversationView === undefined || latestView === null) return null;
        const takenThroughSequence = this.word.events.lastEventSequence;
        const viewChanges = this.viewChangesFor(conversationId, conversationView.view, latestView);
        const owedCards = this.appliedCardsOwedTo(conversationView, latestView);
        /**
         * WHEN the paragraph changes do not fit one message
         *   CONSEQUENCE
         *     only their count rides along; the view stays put, so they stay owed.
         */
        if (viewChanges.kind === "past_budget") {
            return {
                documentChanges: { kind: "too_many_changes", changedParagraphCount: viewChanges.changedParagraphCount, appliedCards: owedCards.appliedCards },
                markDelivered: () => undefined,
            };
        }
        /**
         * WHEN more paragraphs than one message lists came back under new ids
         *   CONSEQUENCE
         *     the whole document as the latest read shows it rides along when it is
         *     short enough, else none and the agent is told to read it again before it
         *     uses an id; then the changes. Once delivered, the agent's view is that
         *     document either way: the agent knows its ids changed.
         */
        if (viewChanges.kind === "paragraph_ids_changed") {
            const changesUnderNewIds: DocumentChange[] = changesNotShownInAppliedCards([...viewChanges.changes, ...this.editsSinceRead(conversationView, latestView)], owedCards.appliedCards);
            return {
                documentChanges: { kind: "paragraph_ids_changed", document: viewChanges.documentRidesAlong ? latestView.paragraphs : null, changes: changesUnderNewIds, unlistedChangedParagraphCount: viewChanges.unlistedChangedParagraphCount, appliedCards: owedCards.appliedCards },
                markDelivered: () => this.rememberView(conversationId, { view: latestView, agentSawEventsThroughSequence: takenThroughSequence, appliedCardsHeldBack: owedCards.reportsHeldBack }),
            };
        }
        /** A paragraph an applied card shows before and after is not listed again beside it (`changesNotShownInAppliedCards`). */
        const changes: DocumentChange[] = changesNotShownInAppliedCards([...viewChanges.changes, ...this.editsSinceRead(conversationView, latestView)], owedCards.appliedCards);
        /**
         * WHEN the conversation's view and the document agree, and no card a read has reached was applied since
         *   CONSEQUENCE
         *     nothing rides along with the message, and every card still owed stays owed.
         */
        if (changes.length === 0 && owedCards.appliedCards.length === 0) return null;
        return {
            documentChanges: { kind: "changes", changes, appliedCards: owedCards.appliedCards },
            markDelivered: () => this.rememberView(conversationId, { view: latestView, agentSawEventsThroughSequence: takenThroughSequence, appliedCardsHeldBack: owedCards.reportsHeldBack }),
        };
    }

    /**
     * What changed from a conversation's view to the latest view.
     *
     * CASES
     *   - the conversation's view is the latest view -> nothing changed, nothing to align
     *   - the latest view's parse prepared them       -> the prepared changes
     *   - no parse prepared them                      -> worked out now and kept (`prepareViewChanges`)
     *
     * WHY a fallback at all
     *   Every view a conversation is given is the latest view of that moment, and
     *   every later parse prepares every conversation, so this path only runs when
     *   that chain breaks; it answers rather than sending a message without its changes.
     */
    private viewChangesFor(conversationId: string, conversationView: DocumentView, latestView: DocumentView): ViewChanges {
        if (conversationView === latestView) return NO_VIEW_CHANGES;
        const prepared = this.preparedViewChangesByConversation.get(conversationId);
        if (prepared !== undefined && prepared.conversationView === conversationView && prepared.latestView === latestView) return prepared.viewChanges;
        return this.prepareViewChanges(conversationId, conversationView, latestView);
    }

    /**
     * Works out what changed for every conversation as soon as a new latest view
     * is parsed, so Send only reads the result.
     *
     * WHY here
     *   The parse already runs in its own task after the reader's work; aligning
     *   two views of a long document is the costly part, and on the Send click it
     *   held the message back (a body pasted over: about half a second).
     *
     * WHEN a conversation's view is the latest view
     *   CONSEQUENCE
     *     nothing is prepared for it: nothing changed since its view.
     */
    private prepareChangesForEveryConversation(latestView: DocumentView): void {
        for (const [conversationId, conversationView] of Array.from(this.viewsByConversation)) {
            if (conversationView.view === latestView) continue;
            this.prepareViewChanges(conversationId, conversationView.view, latestView);
        }
    }

    /** Works out one conversation's changes from its view to the latest view and keeps them with the two views. */
    private prepareViewChanges(conversationId: string, conversationView: DocumentView, latestView: DocumentView): ViewChanges {
        const viewChanges = this.viewChangesBetween(conversationView, latestView);
        this.preparedViewChangesByConversation.set(conversationId, { conversationView, latestView, viewChanges });
        return viewChanges;
    }

    /**
     * The paragraph changes from `earlierView` to `laterView`, or only their count
     * when one message cannot carry them.
     *
     * CASES
     *   - more paragraphs the agent knew stand under new ids
     *     than one message lists (`maximumParagraphChanges`) -> the document lost its bookmarks
     *                                                          wholesale: the changes under new ids
     *                                                          (`viewChangesUnderNewParagraphIds`)
     *   - the views cannot differ by fewer paragraphs than
     *     the budget allows (`fewestParagraphChangesSince`) -> past the budget, counted without aligning them
     *   - aligned, the changes pass the paragraph or the
     *     text budget                                       -> past the budget, with the aligned count
     *   - otherwise                                         -> every change; a paragraph that came back
     *                                                          under a new id is one of them, a
     *                                                          paragraph_changed carrying its old id
     *                                                          and its new one
     *
     * EXAMPLE, for paragraphs under new ids, budget 40
     *   1 paragraph's bookmark replaced                -> one paragraph_changed, old id and new
     *   221 paragraphs' bookmarks stripped by a tool   -> the whole document under its new ids
     *
     * WHY count before aligning
     *   Aligning costs time and memory in step with how many paragraphs differ;
     *   a change set that cannot fit is sent as a count, and the count needs no
     *   alignment.
     *
     * EXAMPLE
     *   2,938 paragraphs pasted over with new wording, budget 40
     *   -> at least 2,938 changes > 40 -> past the budget, `wordDiff` never runs
     */
    private viewChangesBetween(earlierView: DocumentView, laterView: DocumentView): ViewChanges {
        const fewestParagraphChanges = laterView.fewestParagraphChangesSince(earlierView);
        /**
         * WHEN more paragraphs the agent knew stand in the document under new ids than
         * one message lists
         *   CONSEQUENCE
         *     the whole document goes with the message, and the changes are aligned by
         *     Word's own paragraph ids (`viewChangesUnderNewParagraphIds`). Fewer are
         *     listed with the other changes, each with its old id and its new one.
         */
        if (laterView.paragraphIdsChangedSince(earlierView) > this.config.maximumParagraphChanges) return this.viewChangesUnderNewParagraphIds(earlierView, laterView, fewestParagraphChanges);
        if (fewestParagraphChanges > this.config.maximumParagraphChanges) return { kind: "past_budget", changedParagraphCount: fewestParagraphChanges };
        const changes = laterView.changesSince(earlierView);
        const changeBudget = this.budgetOf(changes);
        return changeBudget.fitsOneMessage ? { kind: "within_budget", changes } : { kind: "past_budget", changedParagraphCount: changeBudget.paragraphChangeCount };
    }

    /**
     * The changes when the ids the agent holds no longer name the paragraphs: what
     * changed in the words, comments and tracked changes, for the agent's context.
     *
     * CASES, for the document itself
     *   - its accepted text fits `maximumResentDocumentCharacters` -> it rides along under its new ids
     *   - it is longer                                             -> it stays behind; the agent reads it
     *                                                                 again before it uses an id
     *
     * CASES, for the changes
     *   - the views cannot differ by fewer paragraphs than the
     *     budget allows (`fewestParagraphChangesSince`)         -> none listed, only their count, counted without aligning
     *   - aligned by Word's paragraph ids, the changes pass
     *     the paragraph or the text budget                      -> none listed, only their aligned count
     *   - otherwise                                             -> every change, each paragraph under its new id
     *
     * EXAMPLE
     *   a 221-paragraph body whose bookmarks another tool stripped, then one clause edited
     *   -> 220 paragraphs read the same under new ids, one paragraph_changed listed
     */
    private viewChangesUnderNewParagraphIds(earlierView: DocumentView, laterView: DocumentView, fewestParagraphChanges: number): ViewChangesUnderNewParagraphIds {
        const documentRidesAlong = laterView.acceptedTextCharacterCount() <= this.config.maximumResentDocumentCharacters;
        if (fewestParagraphChanges > this.config.maximumParagraphChanges) return { kind: "paragraph_ids_changed", changes: [], unlistedChangedParagraphCount: fewestParagraphChanges, documentRidesAlong };
        const changes = laterView.changesByWordParagraphIdSince(earlierView);
        const changeBudget = this.budgetOf(changes);
        return changeBudget.fitsOneMessage
            ? { kind: "paragraph_ids_changed", changes, unlistedChangedParagraphCount: 0, documentRidesAlong }
            : { kind: "paragraph_ids_changed", changes: [], unlistedChangedParagraphCount: changeBudget.paragraphChangeCount, documentRidesAlong };
    }

    /** How many paragraphs the changes change (changed, added, deleted) and whether one message can carry them: no more than `maximumParagraphChanges`, their text no longer than `maximumChangedTextCharacters`. */
    private budgetOf(changes: DocumentChange[]): ChangeBudget {
        const paragraphChanges = changes.filter((change) => change.kind === "paragraph_changed" || change.kind === "paragraph_added" || change.kind === "paragraph_deleted");
        const changedTextCharacters = paragraphChanges.reduce((characterCount, change) => characterCount + AgentDocumentViews.textCharactersOf(change), 0);
        return {
            paragraphChangeCount: paragraphChanges.length,
            fitsOneMessage: paragraphChanges.length <= this.config.maximumParagraphChanges && changedTextCharacters <= this.config.maximumChangedTextCharacters,
        };
    }

    /**
     * What one event from the document's stream means for the agents' views.
     *
     * CASES
     *   - Word or the typing detector reports an edit  -> kept as an edit since the latest read (`recordEdit`)
     *   - the add-in replaced the body                 -> the document counts as changed; read when the write ends
     *   - someone in the add-in read the whole body    -> the latest view (`receiveBodyRead`)
     *   - a conversation's agent was handed the body   -> that conversation's view (`receiveCopySentToAgent`)
     *   - the user applied cards                       -> kept for each conversation's next message
     *                                                     (`recordAppliedCards`), and read at once
     *   - the user came back to the pane               -> the document counts as changed; read at once
     */
    private onDocumentEvent(event: DocumentEvent, delivery: DocumentEventDelivery): void {
        switch (event.kind) {
            case "word_change":
                /**
                 * WHEN Word reports a content control added
                 *   CONSEQUENCE
                 *     nothing is recorded: the event names no paragraph, and new text in
                 *     it raises the paragraph events that are recorded.
                 */
                if (event.change.kind === "content_control_added") return;
                this.recordEdit({ sequence: delivery.sequence, edit: event.change.kind, origin: event.change.origin, uniqueLocalIds: event.change.uniqueLocalIds });
                return;
            case "edited_under_cursor":
                this.recordEdit({ sequence: delivery.sequence, edit: "changed", origin: "local", uniqueLocalIds: event.uniqueLocalIds });
                return;
            case "body_replaced":
                this.lastDocumentChangeSequence = delivery.sequence;
                /**
                 * WHEN the add-in has finished replacing the body
                 *   CONSEQUENCE
                 *     the body is read at once: every paragraph id changed with it.
                 */
                if (event.phase === "end") this.scheduleRead(0);
                return;
            case "body_read":
                this.receiveBodyRead(event.read);
                return;
            case "document_sent_to_agent":
                this.receiveCopySentToAgent(event.sentDocument);
                return;
            case "redlines_applied":
                /**
                 * WHEN the user's cards were written
                 *   CONSEQUENCE
                 *     the cards are kept for each conversation's next message, the apply counts
                 *     as a change of the document, and the body is read at once rather than
                 *     after edits settle, so that message shows each card's paragraphs as they
                 *     landed (`appliedCardsForAgent`); a message sent before the read lands goes
                 *     without the cards, which ride with the first message sent after it.
                 *
                 * WHY the apply counts as a change
                 *   Word raises no event for a comment, and a card's own Word events can come
                 *   before the landing audit's read; a read is made only for a change it does
                 *   not yet show, so without this the cards would never be read as they landed.
                 */
                this.recordAppliedCards(delivery.sequence, event.appliedCards);
                this.lastDocumentChangeSequence = delivery.sequence;
                this.scheduleRead(0);
                return;
            case "pane_focused":
                /**
                 * WHEN the user comes back to the pane
                 *   CONSEQUENCE
                 *     the document counts as changed and is read at once: a comment or a
                 *     tracked deletion made in it raised no event, and the read lands
                 *     while the user types, before Send takes the changes.
                 */
                this.lastDocumentChangeSequence = delivery.sequence;
                this.scheduleRead(0);
                return;
        }
    }

    /**
     * Keeps an edit until no conversation is owed it, and reads the body once the burst settles.
     *
     * WHEN the edits kept pass `rememberedEdits`
     *   CONSEQUENCE
     *     the oldest are forgotten. Edits pile up only while reads keep failing, and the
     *     first read that succeeds shows the forgotten edits' paragraphs in its diff.
     *
     * EXAMPLE
     *   rememberedEdits = 1000, the 1001st edit arrives -> slice(-1000) keeps edits 2 to 1001
     */
    private recordEdit(reportedEdit: ReportedEdit): void {
        this.lastDocumentChangeSequence = reportedEdit.sequence;
        this.reportedEdits.push(reportedEdit);
        if (this.reportedEdits.length > this.config.rememberedEdits) this.reportedEdits = this.reportedEdits.slice(-this.config.rememberedEdits);
        this.scheduleRead(this.config.editsSettleMs);
    }

    /**
     * Keeps the cards the user applied until every conversation's next message has
     * carried them (`appliedCardsOwedTo`).
     *
     * WHEN no conversation has a view or awaits one
     *   CONSEQUENCE
     *     nothing is kept: no agent's next message would carry them.
     */
    private recordAppliedCards(sequence: number, appliedCards: AppliedCardReport[]): void {
        for (const appliedCard of appliedCards) this.reportedAppliedCards.push({ sequence, appliedCard });
        this.forgetAppliedCardsNoConversationIsOwed();
    }

    /**
     * The cards the user applied after this conversation's agent last saw the
     * document, and the cards an earlier message went without, one entry per card,
     * each paragraph read from the conversation's view and the latest view, with the
     * reports of every card no read has reached yet held back (`appliedCardsForAgent`).
     *
     * EXAMPLE
     *   agent saw through 12, a message went without the card at 11; cards applied at 9 and at 14
     *   -> the ones at 11 and 14
     */
    private appliedCardsOwedTo(conversationView: ConversationView, latestView: DocumentView): AppliedCardsForMessage {
        const reportedSinceTheAgentSaw = this.reportedAppliedCards.filter((reported) => reported.sequence > conversationView.agentSawEventsThroughSequence);
        return appliedCardsForAgent([...conversationView.appliedCardsHeldBack, ...reportedSinceTheAgentSaw], { conversationView: conversationView.view, latestView });
    }

    /**
     * Drops every applied card no conversation's next message can still carry.
     *
     * CASES, a card is kept while
     *   - some conversation's agent has not seen the events through it
     *   - a copy handed to an agent awaits its view and the card came after the
     *     copy: that agent's view will start from the copy
     */
    private forgetAppliedCardsNoConversationIsOwed(): void {
        const conversationViews = Array.from(this.viewsByConversation.values());
        const awaitingCopySequences = Array.from(this.copiesAwaitingView.values());
        this.reportedAppliedCards = this.reportedAppliedCards.filter((reported) => {
            const owedToAConversation = conversationViews.some((conversationView) => reported.sequence > conversationView.agentSawEventsThroughSequence);
            const mayFallAfterAnAwaitedCopy = awaitingCopySequences.some((copySequence) => reported.sequence > copySequence);
            return owedToAConversation || mayFallAfterAnAwaitedCopy;
        });
    }

    /**
     * Takes a whole-body read as the latest view, parsing it after the reader's
     * own work: the reader may be Apply All, which should not wait on the parse.
     *
     * WHEN a newer read arrives before the parse ran
     *   CONSEQUENCE
     *     only the newer one is parsed.
     */
    private receiveBodyRead(bodyRead: BodyRead): void {
        this.lastReadCoversEventsThroughSequence = Math.max(this.lastReadCoversEventsThroughSequence, bodyRead.coversEventsThroughSequence);
        const awaitingRead = this.bodyReadAwaitingParse;
        if (awaitingRead === null || awaitingRead.coversEventsThroughSequence <= bodyRead.coversEventsThroughSequence) this.bodyReadAwaitingParse = bodyRead;
        if (this.parseTimer !== null) return;
        this.parseTimer = window.setTimeout(() => {
            this.parseTimer = null;
            this.parseAwaitingRead();
        }, 0);
    }

    /**
     * Turns the newest read into the latest view, hands it to every
     * conversation whose copy it covers, and works out what changed for every
     * other conversation (`prepareChangesForEveryConversation`).
     *
     * CASES
     *   - no read awaits                        -> nothing to do
     *   - the read is older than the latest view -> dropped
     *   - the reader fails on the package        -> the latest view stays; the error goes to Sentry
     */
    private parseAwaitingRead(): void {
        const bodyRead = this.bodyReadAwaitingParse;
        this.bodyReadAwaitingParse = null;
        if (bodyRead === null) return;
        if (this.latestView !== null && bodyRead.coversEventsThroughSequence < this.latestView.coversEventsThroughSequence) return;
        try {
            this.latestView = DocumentView.fromBodyRead(bodyRead);
        } catch (error) {
            reportError(error, { tags: { component: "AgentDocumentViews", action: "parse_body_read" } });
            return;
        }
        this.settleCopiesCoveredBy(this.latestView, "view_must_cover_copy");
        this.forgetEditsNoConversationIsOwed();
        this.prepareChangesForEveryConversation(this.latestView);
    }

    /**
     * A conversation's agent was handed the whole document.
     *
     * CASES
     *   - the latest view already covers the copy, or nothing
     *     changed the document since the latest view was read -> the copy's view is the latest view
     *   - otherwise                                             -> the body is read at once, and the
     *                                                              copy's view is the first read that
     *                                                              covers it
     */
    private receiveCopySentToAgent(sentDocument: DocumentSentToAgent): void {
        this.copiesAwaitingView.set(sentDocument.conversationId, sentDocument.coversEventsThroughSequence);
        const latestView = this.latestView;
        const latestViewIsCurrent = latestView !== null && this.lastDocumentChangeSequence <= latestView.coversEventsThroughSequence;
        if (latestView !== null && (latestViewIsCurrent || latestView.coversEventsThroughSequence >= sentDocument.coversEventsThroughSequence)) {
            this.settleCopiesCoveredBy(latestView, latestViewIsCurrent ? "view_is_current" : "view_must_cover_copy");
            return;
        }
        this.scheduleRead(0);
    }

    /**
     * Gives every copy awaiting a view the view that covers it.
     *
     * WHY the agent's own sequence is kept
     *   A view read after the copy shows edits the agent never saw. They are not
     *   in any later diff against this view, so they are reported as edits since
     *   read: everything after the copy's sequence the diff does not show.
     *
     * EXAMPLE
     *   copy covers 12, view covers 15, edits 13 and 14 reported in between
     *   -> the conversation's view is this view, its agent saw through 12;
     *      13 and 14 are listed by paragraph with the next message
     */
    private settleCopiesCoveredBy(view: DocumentView, copyCoverage: CopyCoverage): void {
        for (const [conversationId, copyCoversEventsThroughSequence] of Array.from(this.copiesAwaitingView)) {
            /**
             * WHEN the view was read before the copy and something changed the document since
             *   CONSEQUENCE
             *     this copy keeps waiting for a read that began after it.
             */
            if (copyCoverage === "view_must_cover_copy" && view.coversEventsThroughSequence < copyCoversEventsThroughSequence) continue;
            this.copiesAwaitingView.delete(conversationId);
            this.rememberView(conversationId, { view, agentSawEventsThroughSequence: copyCoversEventsThroughSequence, appliedCardsHeldBack: [] });
        }
    }

    /**
     * Keeps a conversation's view, the most recently used last, and forgets the
     * least recently used beyond `rememberedConversations`.
     *
     * EXAMPLE
     *   rememberedConversations = 4, a fifth conversation's view arrives
     *   -> size 5 - 4 = 1 view forgotten: the first in the map, used longest ago
     */
    private rememberView(conversationId: string, conversationView: ConversationView): void {
        this.viewsByConversation.delete(conversationId);
        this.preparedViewChangesByConversation.delete(conversationId);
        this.viewsByConversation.set(conversationId, conversationView);
        while (this.viewsByConversation.size > this.config.rememberedConversations) {
            const leastRecentlyUsed = this.viewsByConversation.keys().next().value;
            /**
             * WHEN the map is over the limit yet yields no first key
             *   CONSEQUENCE
             *     the loop stops instead of spinning; Map iteration yields a key while entries remain.
             */
            if (leastRecentlyUsed === undefined) break;
            this.viewsByConversation.delete(leastRecentlyUsed);
            this.preparedViewChangesByConversation.delete(leastRecentlyUsed);
        }
        this.forgetEditsNoConversationIsOwed();
        this.forgetAppliedCardsNoConversationIsOwed();
    }

    /**
     * Drops every edit no conversation can still be owed.
     *
     * CASES, an edit is kept while
     *   - some conversation's next message must list it (`editIsOwed`)
     *   - a copy handed to an agent awaits its view and the edit came after the
     *     copy: it may fall between the copy and the read that becomes its view
     *
     * WHY
     *   An edit a later read shows is in that conversation's diff; kept, every
     *   keystroke between two messages would stay for the session.
     */
    private forgetEditsNoConversationIsOwed(): void {
        const latestView = this.latestView;
        const conversationViews = Array.from(this.viewsByConversation.values());
        const awaitingCopySequences = Array.from(this.copiesAwaitingView.values());
        this.reportedEdits = this.reportedEdits.filter((reportedEdit) => {
            const owedToAConversation = latestView !== null && conversationViews.some((conversationView) => AgentDocumentViews.editIsOwed(reportedEdit, conversationView, latestView));
            const mayFallInsideAnAwaitedView = awaitingCopySequences.some((copySequence) => reportedEdit.sequence > copySequence);
            return owedToAConversation || mayFallInsideAnAwaitedView;
        });
    }

    /**
     * Whether a conversation's next message must list this edit by paragraph.
     *
     * CASES, for an edit after what the conversation's agent saw
     *   - between the conversation's view and the latest view -> no: the diff shows it
     *   - before the conversation's view was read, or after
     *     the latest view was read                            -> yes
     *
     * EXAMPLE
     *   agent saw 12, its view covers 15, the latest view covers 30
     *   edit 9  -> no: seen       edit 14 -> yes: inside its own view, in no diff
     *   edit 20 -> no: diffed     edit 31 -> yes: no read shows it yet
     */
    private static editIsOwed(reportedEdit: ReportedEdit, conversationView: ConversationView, latestView: DocumentView): boolean {
        const agentSawIt = reportedEdit.sequence <= conversationView.agentSawEventsThroughSequence;
        const diffShowsIt = conversationView.view.coversEventsThroughSequence < reportedEdit.sequence && reportedEdit.sequence <= latestView.coversEventsThroughSequence;
        return !agentSawIt && !diffShowsIt;
    }

    /**
     * The edits this conversation is owed that no diff shows (`editIsOwed`),
     * grouped by what Word reported and who made them, each paragraph named once.
     *
     * CASES, for a paragraph an edit names
     *   - the latest view read it -> named by its paragraph bookmark or position
     *   - it did not              -> counted as unidentified: added after the read
     */
    private editsSinceRead(conversationView: ConversationView, latestView: DocumentView): DocumentChange[] {
        const owedEdits = this.reportedEdits.filter((reportedEdit) => AgentDocumentViews.editIsOwed(reportedEdit, conversationView, latestView));
        const groupsByKindAndOrigin = new Map<string, OwedEditGroup>();
        for (const owedEdit of owedEdits) {
            const groupKey = `${owedEdit.edit}:${owedEdit.origin}`;
            const group = groupsByKindAndOrigin.get(groupKey) ?? { edit: owedEdit.edit, origin: owedEdit.origin, paragraphByUniqueLocalId: new Map(), unidentifiedUniqueLocalIds: new Set() };
            groupsByKindAndOrigin.set(groupKey, group);
            for (const uniqueLocalId of owedEdit.uniqueLocalIds) {
                const paragraph = latestView.paragraphWithUniqueLocalId(uniqueLocalId);
                if (paragraph === null) group.unidentifiedUniqueLocalIds.add(uniqueLocalId);
                else group.paragraphByUniqueLocalId.set(uniqueLocalId, DocumentView.referenceTo(paragraph));
            }
        }
        return Array.from(groupsByKindAndOrigin.values()).map((group) => ({
            kind: "edited_since_read",
            edit: group.edit,
            origin: group.origin,
            paragraphs: Array.from(group.paragraphByUniqueLocalId.values()),
            unidentifiedParagraphCount: group.unidentifiedUniqueLocalIds.size,
        }));
    }

    /** One timer: a new schedule replaces the pending one, so a burst of edits reads once. */
    private scheduleRead(delayMs: number): void {
        if (this.readTimer !== null) window.clearTimeout(this.readTimer);
        this.readTimer = window.setTimeout(() => {
            this.readTimer = null;
            void this.readNow();
        }, delayMs);
    }

    /**
     * Reads the whole body; the read is published on the stream and comes back
     * here as `body_read`.
     *
     * CASES
     *   - no conversation has a view or awaits one -> no read: nothing to compare against
     *   - no read is owed since the last read      -> no read
     *   - a read is in flight                      -> this one runs after it
     *   - foreground work is pending in Word       -> stand aside, ask again shortly
     *   - the read fails                           -> tried again after a backoff (`failedReadRetryMs`)
     */
    private async readNow(): Promise<void> {
        const someConversationFollows = this.viewsByConversation.size > 0 || this.copiesAwaitingView.size > 0;
        const documentChangedSinceRead = this.latestView === null || this.lastDocumentChangeSequence > this.lastReadCoversEventsThroughSequence;
        const copyAwaitsRead = Array.from(this.copiesAwaitingView.values()).some((copySequence) => copySequence > this.lastReadCoversEventsThroughSequence);
        if (!someConversationFollows || (!documentChangedSinceRead && !copyAwaitsRead)) return;
        if (this.readInFlight) {
            this.readQueued = true;
            return;
        }
        if (this.word.pendingForegroundWork() > 0) {
            this.scheduleRead(this.config.foregroundRetryMs);
            return;
        }
        this.readInFlight = true;
        try {
            await this.word.withRead((session) => session.readBody());
            this.readFailures = 0;
        } catch {
            this.readFailures += 1;
            this.scheduleRead(failedReadRetryMs(this.readFailures));
        } finally {
            this.readInFlight = false;
            if (this.readQueued) {
                this.readQueued = false;
                this.scheduleRead(0);
            }
        }
    }

    /** The paragraph text a change carries: both versions of a changed paragraph, the one version of an added or deleted one. */
    private static textCharactersOf(change: DocumentChange): number {
        switch (change.kind) {
            case "paragraph_changed":
                return change.before.allChangesAcceptedText.length + change.after.allChangesAcceptedText.length;
            case "paragraph_added":
                return change.after.allChangesAcceptedText.length;
            case "paragraph_deleted":
                return change.before.allChangesAcceptedText.length;
            default:
                return 0;
        }
    }
}
